import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";

import Divider from "@material-ui/core/Divider";
import { Badge, Tooltip } from "@material-ui/core";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import ForumIcon from "@material-ui/icons/Forum";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import LoyaltyRoundedIcon from "@material-ui/icons/LoyaltyRounded";
import { Can } from "../components/Can";
import { SocketContext } from "../context/Socket/SocketContext";
import { isArray } from "lodash";
import api from "../services/api";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import toastError from "../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import { Schedule } from "@material-ui/icons";
import usePlans from "../hooks/usePlans";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: 12,
    color: theme.palette.sidebarIcon,
    transition: "all 0.15s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.sidebarHover,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 22,
    },
  },
  iconButtonActive: {
    color: theme.palette.sidebarIconActive,
    backgroundColor: theme.palette.sidebarActive,
  },
  iconButtonExpanded: {
    width: "auto",
    justifyContent: "flex-start",
    padding: "0 16px",
    margin: "4px 8px",
    borderRadius: 8,
  },
}));


function IconNavItem({ icon, label, to, badge, onClick, collapsed = true }) {
  const classes = useStyles();
  const location = useLocation();
  const isActive = location.pathname === to;

  const content = (
    <div
      className={`${classes.iconButton} ${isActive ? classes.iconButtonActive : ""} ${!collapsed ? classes.iconButtonExpanded : ""}`}
      onClick={onClick}
    >
      {badge != null && badge > 0 ? (
        <Badge badgeContent={badge} color="secondary" variant="dot">
          {icon}
        </Badge>
      ) : (
        icon
      )}
      {!collapsed && <span style={{ marginLeft: 16, fontWeight: 500, fontSize: "0.95rem" }}>{label}</span>}
    </div>
  );

  const wrappedContent = collapsed ? (
    <Tooltip title={label} placement="right" arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );

  if (to) {
    return (
      <RouterLink to={to} style={{ textDecoration: "none" }}>
        {wrappedContent}
      </RouterLink>
    );
  }
  return wrappedContent;
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = (props) => {
  const classes = useStyles();
  const { drawerClose, collapsed, onSettingsClick, onProfileClick } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, handleLogout } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const history = useHistory();
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const { getPlanCompany } = usePlans();

  const socketManager = useContext(SocketContext);
 

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowKanban(planConfigs.plan.useKanban);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleChatEvent = (data) => {
      if (data.action === "new-message") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
      if (data.action === "update") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
    };

    socket.on(`company-${companyId}-chat`, handleChatEvent);

    return () => {
      socket.off(`company-${companyId}-chat`, handleChatEvent);
    };
  }, [socketManager]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const handleClickLogout = () => {
    handleLogout();
  };

  // For non-collapsed mode (mobile drawer), render full list
  if (!collapsed) {
    return (
      <div onClick={drawerClose}>
        <IconNavItem collapsed={false} to="/tickets" icon={<WhatsAppIcon />} label={i18n.t("mainDrawer.listItems.tickets")} />
        <IconNavItem collapsed={false} to="/quick-messages" icon={<FlashOnIcon />} label={i18n.t("mainDrawer.listItems.quickMessages")} />
        {showKanban && <IconNavItem collapsed={false} to="/kanban" icon={<LoyaltyRoundedIcon />} label="Kanban" />}
        <IconNavItem collapsed={false} to="/todolist" icon={<BorderColorIcon />} label={i18n.t("Tarefas")} />
        <IconNavItem collapsed={false} to="/contacts" icon={<ContactPhoneOutlinedIcon />} label={i18n.t("mainDrawer.listItems.contacts")} />
        {showSchedules && <IconNavItem collapsed={false} to="/schedules" icon={<Schedule />} label={i18n.t("mainDrawer.listItems.schedules")} />}
        <IconNavItem collapsed={false} to="/tags" icon={<LocalOfferIcon />} label={i18n.t("mainDrawer.listItems.tags")} />
        {showInternalChat && (
          <IconNavItem
            collapsed={false}
            to="/chats"
            icon={<Badge color="secondary" variant="dot" invisible={invisible}><ForumIcon /></Badge>}
            label={i18n.t("mainDrawer.listItems.chats")}
          />
        )}
        <IconNavItem collapsed={false} to="/helps" icon={<HelpOutlineIcon />} label={i18n.t("mainDrawer.listItems.helps")} />
        <Can
          role={user.profile}
          perform="drawer-admin-items:view"
          yes={() => (
            <IconNavItem collapsed={false} icon={<SettingsOutlinedIcon />} label={i18n.t("mainDrawer.listItems.settings")} onClick={() => onSettingsClick && onSettingsClick()} />
          )}
        />
        <IconNavItem collapsed={false} icon={<AccountCircleIcon />} label={i18n.t("mainDrawer.appBar.user.profile")} onClick={() => onProfileClick && onProfileClick()} />
        <IconNavItem collapsed={false} icon={<ExitToAppIcon style={{ color: "#ea0038" }} />} label={i18n.t("Sair")} onClick={handleClickLogout} />
      </div>
    );
  }

  // Collapsed mode (icon-only sidebar)
  return (
    <>
      <IconNavItem to="/tickets" icon={<WhatsAppIcon />} label={i18n.t("mainDrawer.listItems.tickets")} />
      <IconNavItem to="/quick-messages" icon={<FlashOnIcon />} label={i18n.t("mainDrawer.listItems.quickMessages")} />
      {showKanban && <IconNavItem to="/kanban" icon={<LoyaltyRoundedIcon />} label="Kanban" />}
      <IconNavItem to="/todolist" icon={<BorderColorIcon />} label={i18n.t("Tarefas")} />
      <IconNavItem to="/contacts" icon={<ContactPhoneOutlinedIcon />} label={i18n.t("mainDrawer.listItems.contacts")} />
      {showSchedules && <IconNavItem to="/schedules" icon={<Schedule />} label={i18n.t("mainDrawer.listItems.schedules")} />}
      <IconNavItem to="/tags" icon={<LocalOfferIcon />} label={i18n.t("mainDrawer.listItems.tags")} />
      {showInternalChat && (
        <IconNavItem
          to="/chats"
          icon={<Badge color="secondary" variant="dot" invisible={invisible}><ForumIcon /></Badge>}
          label={i18n.t("mainDrawer.listItems.chats")}
        />
      )}
      <IconNavItem to="/helps" icon={<HelpOutlineIcon />} label={i18n.t("mainDrawer.listItems.helps")} />

      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider style={{ width: "70%", margin: "4px 0" }} />
            <IconNavItem
              icon={
                <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                  <SettingsOutlinedIcon />
                </Badge>
              }
              label="Configurações"
              onClick={() => onSettingsClick && onSettingsClick()}
            />
          </>
        )}
      />

      <Tooltip title={i18n.t("Sair")} placement="right">
        <div className={classes.iconButton} onClick={handleClickLogout} style={{ color: "#ea0038" }}>
          <ExitToAppIcon />
        </div>
      </Tooltip>
    </>
  );
};

export default MainListItems;
